import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Button,
    Dialog,
    IconButton,
    DialogContent,
    makeStyles,
} from '@material-ui/core';
import { colors } from '../../constants.js/colors';
import CloseIcon from '@material-ui/icons/Close';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import TuneIcon from '@material-ui/icons/Tune';
import CategoryIcon from '@material-ui/icons/Category';
import StorefrontIcon from '@material-ui/icons/Storefront';

import { routes } from '../../constants.js/misc';
import {
    SUBCATEGORIES_FEATURE_YOUTUBE_ID,
    WHOLESALE_PRICING_ONBOARDING,
} from '../../constants/subcategoriesFeatureVideo';
import {
    ModalPaper,
    Hero,
    Badge,
    HeroTitle,
    HeroSubtitle,
    CloseButtonWrap,
    Body,
    ContentSlot,
    VideoFrame,
    HintList,
    Actions,
    ActionsGroup,
    ActionPrev,
    ActionLink,
    ActionPrimary,
    StepIndicator,
    StepDot,
    BenefitCardsGrid,
    BenefitCard,
    BenefitIconWrap,
    BenefitText,
    BenefitTitle,
    BenefitDescription,
    ExclusiveCallout,
    ExclusiveCalloutBadge,
    ExclusiveCalloutText,
} from './SubcategoriesFeatureVideoModal.styles';

const EMBED_URL = `https://www.youtube.com/embed/${SUBCATEGORIES_FEATURE_YOUTUBE_ID}?rel=0&modestbranding=1`;

const WHOLESALE_BENEFIT_ICONS = {
    wholesale: LocalOfferIcon,
    discount: TuneIcon,
    scope: CategoryIcon,
    automatic: StorefrontIcon,
};

const useStyles = makeStyles({
    outlinedBtn: {
        borderWidth: 2,
        borderColor: `${colors.gray.light} !important`,
        color: colors.primary.dark,
        fontWeight: 600,
        textTransform: 'none',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(158, 232, 112, 0.4) !important',
            borderColor: `${colors.primary.main} !important`,
            borderWidth: '2px !important',
            color: `${colors.primary.dark} !important`,
            boxShadow: '0 4px 12px rgba(115, 237, 184, 0.35)',
        },
    },
    containedBtn: {
        fontWeight: 600,
        textTransform: 'none',
        color: colors.primary.dark,
        boxShadow: '0 4px 14px rgba(23, 51, 0, 0.22)',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
        '&:hover': {
            backgroundColor: '#85d85a',
            boxShadow: '0 6px 18px rgba(23, 51, 0, 0.3)',
            transform: 'translateY(-1px)',
        },
    },
    closeBtn: {
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
        },
    },
});

/** Popup unificado al login: 1) subcategorías (video) → 2) precios mayoristas (texto). */
const SubcategoriesFeatureVideoModal = ({ open, onClose }) => {
    const classes = useStyles();
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (open) setStep(0);
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const handleCollections = () => {
        onClose();
    };

    const handleGoToPromotions = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ style: ModalPaper }}
            aria-labelledby="feature-onboarding-dialog-title"
        >
            <DialogContent style={{ padding: 0 }}>
                {step === 0 ? (
                    <>
                        <Hero>
                            <CloseButtonWrap>
                                <IconButton
                                    aria-label="Cerrar"
                                    onClick={handleClose}
                                    size="small"
                                    className={classes.closeBtn}
                                    style={{ color: '#fff' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </CloseButtonWrap>
                            <Badge>Nuevo</Badge>
                            <HeroTitle id="feature-onboarding-dialog-title">
                                Subcategorías en tu catálogo
                            </HeroTitle>
                            <HeroSubtitle>
                                Organiza tus productos con más detalle. Mira cómo crear subcategorías y
                                cómo se ven en tu tienda.
                            </HeroSubtitle>
                        </Hero>
                        <Body>
                            <ContentSlot>
                                <VideoFrame>
                                    <iframe
                                        src={EMBED_URL}
                                        title="Cómo funcionan las subcategorías en ecometri"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    />
                                </VideoFrame>
                                <HintList>
                                    <li>Crea subcategorías dentro de una categoría desde Categorías.</li>
                                    <li>
                                        Tus clientes verán botones de subcategoría para navegar entre ellas en la
                                        tienda.
                                    </li>
                                    <li>Ideal para agrupar variantes, líneas o tipos de producto.</li>
                                </HintList>
                            </ContentSlot>
                            <StepIndicator>
                                <StepDot active />
                                <span>1/2</span>
                                <StepDot />
                            </StepIndicator>
                            <Actions>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.outlinedBtn}
                                    component={RouterLink}
                                    to={routes.collectionsPage}
                                    onClick={handleCollections}
                                >
                                    Ir a categorías
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.containedBtn}
                                    onClick={() => setStep(1)}
                                >
                                    Siguiente
                                </Button>
                            </Actions>
                        </Body>
                    </>
                ) : (
                    <>
                        <Hero>
                            <CloseButtonWrap>
                                <IconButton
                                    aria-label="Cerrar"
                                    onClick={handleClose}
                                    size="small"
                                    className={classes.closeBtn}
                                    style={{ color: '#fff' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </CloseButtonWrap>
                            <Badge>{WHOLESALE_PRICING_ONBOARDING.badge}</Badge>
                            <HeroTitle>{WHOLESALE_PRICING_ONBOARDING.title}</HeroTitle>
                            <HeroSubtitle>{WHOLESALE_PRICING_ONBOARDING.subtitle}</HeroSubtitle>
                        </Hero>
                        <Body>
                            <ContentSlot>
                                <BenefitCardsGrid>
                                    {WHOLESALE_PRICING_ONBOARDING.benefits.map((benefit) => {
                                        const Icon = WHOLESALE_BENEFIT_ICONS[benefit.id];
                                        return (
                                            <BenefitCard key={benefit.id}>
                                                <BenefitIconWrap>
                                                    {Icon ? <Icon aria-hidden /> : null}
                                                </BenefitIconWrap>
                                                <BenefitText>
                                                    <BenefitTitle>{benefit.title}</BenefitTitle>
                                                    <BenefitDescription>
                                                        {benefit.description}
                                                    </BenefitDescription>
                                                </BenefitText>
                                            </BenefitCard>
                                        );
                                    })}
                                </BenefitCardsGrid>
                                <ExclusiveCallout>
                                    <ExclusiveCalloutBadge>
                                        {WHOLESALE_PRICING_ONBOARDING.desktopCallout.badge}
                                    </ExclusiveCalloutBadge>
                                    <ExclusiveCalloutText>
                                        {WHOLESALE_PRICING_ONBOARDING.desktopCallout.message}
                                    </ExclusiveCalloutText>
                                </ExclusiveCallout>
                            </ContentSlot>
                            <StepIndicator>
                                <StepDot />
                                <span>2/2</span>
                                <StepDot active />
                            </StepIndicator>
                            <Actions $spread>
                                <ActionPrev>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        className={classes.outlinedBtn}
                                        onClick={() => setStep(0)}
                                    >
                                        Anterior
                                    </Button>
                                </ActionPrev>
                                <ActionsGroup>
                                    <ActionLink>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            className={classes.outlinedBtn}
                                            component={RouterLink}
                                            to={routes.promotionsPage}
                                            onClick={handleGoToPromotions}
                                        >
                                            Ir a Precios Mayoristas
                                        </Button>
                                    </ActionLink>
                                    <ActionPrimary>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={classes.containedBtn}
                                            onClick={handleClose}
                                        >
                                            Entendido
                                        </Button>
                                    </ActionPrimary>
                                </ActionsGroup>
                            </Actions>
                        </Body>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SubcategoriesFeatureVideoModal;
